/**
 * Centralized analytics service — Powtoon BITracker pattern.
 * Dispatches every event to GA4 (gtag), GTM dataLayer, and Logz.io.
 * Graceful no-op when VITE_GA_MEASUREMENT_ID is not configured.
 */

import { createLogger } from "@/lib/frontend-logger";
import type { AnalyticsCategory, AnalyticsAction } from "@/config/analytics-config";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}

const logger = createLogger("Analytics");

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const APP_NAME = "live-avatar-front";

let initialized = false;

export function initAnalytics(): void {
  if (initialized) return;
  initialized = true;

  window.dataLayer = window.dataLayer || [];

  if (!GA_MEASUREMENT_ID) {
    logger.warn("GA4 Measurement ID not configured — analytics events will be logged only");
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.onload = () => logger.info("gtag.js script loaded successfully");
  script.onerror = (e) => logger.error("gtag.js script failed to load", { error: String(e) });
  document.head.appendChild(script);

  // eslint-disable-next-line prefer-rest-params
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments as unknown as Record<string, unknown>);
  };

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    page_path: window.location.pathname,
    app_name: APP_NAME,
  });

  logger.info("GA4 initialised", { measurementId: GA_MEASUREMENT_ID });
}

interface AnalyticsEvent {
  category: AnalyticsCategory;
  action: AnalyticsAction;
  label?: string;
  value?: number;
  extraData?: Record<string, unknown>;
  ga4EventName?: string;
}

export function trackEvent(event: AnalyticsEvent): void {
  try {
    const { category, action, label, value, extraData, ga4EventName } = event;
    const eventName = ga4EventName || `${category}_${action}`;

    if (window.gtag) {
      window.gtag("event", eventName, {
        event_category: category,
        event_label: label,
        value,
        ...extraData,
      });
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        category,
        action,
        label,
        value,
        ...extraData,
      });
    }

    logger.debug(`Event: ${eventName}`, { category, action, label, value, extraData });
  } catch (error) {
    logger.error("Failed to track event", {
      error: error instanceof Error ? error.message : String(error),
      event,
    });
  }
}

export function trackPageView(pageName: string): void {
  try {
    if (window.gtag && GA_MEASUREMENT_ID) {
      window.gtag("config", GA_MEASUREMENT_ID, {
        page_title: document.title,
        page_path: window.location.pathname,
      });
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: "page_view",
        page_name: pageName,
        page_path: window.location.pathname,
      });
    }

    logger.debug(`PageView: ${pageName}`, { path: window.location.pathname });
  } catch (error) {
    logger.error("Failed to track page view", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export function setAnalyticsUser(
  userId: number | string,
  traits?: Record<string, unknown>,
): void {
  try {
    if (window.gtag) {
      window.gtag("set", { user_id: String(userId), ...traits });
    }
    logger.debug("Analytics user set", { userId });
  } catch (error) {
    logger.error("Failed to set analytics user", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export function clearAnalyticsUser(): void {
  try {
    if (window.gtag) {
      window.gtag("set", { user_id: undefined });
    }
    logger.debug("Analytics user cleared");
  } catch (error) {
    logger.error("Failed to clear analytics user", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
